import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import config from '../services/config-service';
import type { ApolloGqlJobFunnel, ApolloGqlAccountManager, StatusType } from '@bloomays-lib/types.shared';

// Durée de validité du cache en millisecondes (1 minute = 60000ms)
export const CACHE_TTL_MS = 60000; // 1 minute

const { release: CURRENT_RELEASE } = config();

export type StatusFilter = StatusType | 'all';

export type JobListState = {
  jobs: ApolloGqlJobFunnel[] | null;
  loading: boolean;
  error?: Error;
  lastFetchedAt: number | null;
  sortAscending: boolean;
  searchQuery: string;
  statusFilter: StatusFilter;
  tamFilter: string | null;
  tamList: ApolloGqlAccountManager[];
  release: string | null;
  setJobs: (jobs: ApolloGqlJobFunnel[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: Error | undefined) => void;
  setSortAscending: (sortAscending: boolean) => void;
  setSearchQuery: (searchQuery: string) => void;
  setStatusFilter: (statusFilter: StatusFilter) => void;
  setTamFilter: (tamFilter: string | null) => void;
  setTamList: (tamList: ApolloGqlAccountManager[]) => void;
  getJobById: (id: string) => ApolloGqlJobFunnel | undefined;
  mustRefreshJobList: () => boolean;
  reset: () => void;
};

export const useJobListStore = create<JobListState>()(
  persist(
    (set, get) => ({
      jobs: null,
      loading: false,
      error: undefined,
      lastFetchedAt: null,
      sortAscending: true,
      searchQuery: '',
      statusFilter: 'all',
      tamFilter: null,
      tamList: [],
      release: null,

      setJobs: (jobs: ApolloGqlJobFunnel[]) =>
        set({
          jobs,
          error: undefined,
          lastFetchedAt: Date.now(),
          release: CURRENT_RELEASE,
        }),

      setLoading: (loading: boolean) => set({ loading }),

      setError: (error: Error | undefined) => set({ error, loading: false }),

      setSortAscending: (sortAscending: boolean) => set({ sortAscending }),

      setSearchQuery: (searchQuery: string) => set({ searchQuery }),

      setStatusFilter: (statusFilter: StatusFilter) => set({ statusFilter }),

      setTamFilter: (tamFilter: string | null) => set({ tamFilter }),

      setTamList: (tamList: ApolloGqlAccountManager[]) => set({ tamList }),

      getJobById: (id: string) => {
        const { jobs, release } = get();
        if (!jobs) return undefined;
        // Invalide les données persistées provenant d'une autre release
        if (release !== CURRENT_RELEASE) {
          return undefined;
        }
        return jobs.find((job) => job.id === id);
      },

      // Vérifie si le cache est encore valide
      mustRefreshJobList: () => {
        const { jobs, lastFetchedAt, release } = get();

        // Pas de jobs ou pas de timestamp = cache invalide
        if (jobs === null || lastFetchedAt === null) {
          return true;
        }

        // Release différente = cache invalide
        if (release !== CURRENT_RELEASE) {
          return true;
        }

        // Vérifie si la durée de validité du localStorage n'a pas expiré
        const now = Date.now();
        const elapsed = now - lastFetchedAt;
        const isInvalid = elapsed > CACHE_TTL_MS;
        return isInvalid;
      },

      reset: () =>
        set({
          jobs: null,
          loading: false,
          error: undefined,
          lastFetchedAt: null,
          sortAscending: true,
          searchQuery: '',
          statusFilter: 'all',
          tamFilter: null,
          tamList: [],
          release: null,
        }),
    }),
    {
      name: 'bloomays-joblist-cache',
      partialize: (state) => ({
        jobs: state.jobs,
        lastFetchedAt: state.lastFetchedAt, // ← Persiste aussi le timestamp
        sortAscending: state.sortAscending, // ← Persiste l'ordre de tri
        searchQuery: state.searchQuery, // ← Persiste la recherche
        statusFilter: state.statusFilter, // ← Persiste le filtre de statut
        tamFilter: state.tamFilter, // ← Persiste le filtre TAM
        release: state.release,
      }),
    },
  ),
);

export default useJobListStore;
